import {AuthService} from "./AuthService";

export class MarketplaceProductService {
    url = `campaigns/`;

    connect(campaignId, marketplace, credentials) {
        return AuthService.apiPost(this.url + campaignId + "/marketplace_connect/", {
            marketplace: marketplace,
            credentials: credentials,
        }).then(res => res.data.data);
    }

    getIntegration(campaignId) {
        return AuthService.apiGet(this.url + campaignId + "/marketplace_integration/")
            .then(res => res.data.data);
    }

    // Self-service "Connect with Amazon": begin the OAuth round for the
    // country the seller picked. Returns {consent_url} -- the caller
    // navigates the browser there; the backend has already generated and
    // stored the single-use state (see campaign/views_campaign.py
    // amazon_oauth_start / product/marketplace_api/service_amazon_oauth.py).
    startAmazonOAuth(campaignId, marketplaceId) {
        return AuthService.apiPost(this.url + campaignId + "/amazon_oauth_start/", {
            marketplace_id: marketplaceId,
        }).then(res => res.data.data);
    }

    // Completes the OAuth round after Amazon redirects back to
    // /amazon/callback (see AmazonOAuthCallback.js) with spapi_oauth_code,
    // state and selling_partner_id. Collection-level (no campaign id in the
    // URL) -- the state alone tells the backend which campaign this is.
    completeAmazonOAuth(state, code, sellingPartnerId) {
        return AuthService.apiPost(this.url + "amazon_oauth_callback/", {
            state: state, code: code, selling_partner_id: sellingPartnerId,
        }).then(res => res.data.data);
    }

    // Admin-only on the backend (campaign/views_campaign.py marketplace_refresh_interval
    // returns 403 for non-admins); the panel only renders the control that calls
    // this for admins (Task 19), but the guard is enforced server-side regardless.
    setRefreshInterval(campaignId, minutes) {
        return AuthService.apiPost(this.url + campaignId + "/marketplace_refresh_interval/", {
            minutes: minutes,
        }).then(res => res.data.data);
    }

    // Admin-only on the backend (campaign/views_campaign.py marketplace_settle_interval
    // returns 403 for non-admins); the panel only renders the control that calls
    // this for admins, but the guard is enforced server-side regardless. Pass
    // `minutes: null` to clear the per-campaign override and restore the
    // shipped default settle floor.
    setSettleInterval(campaignId, minutes) {
        return AuthService.apiPost(this.url + campaignId + "/marketplace_settle_interval/", {
            minutes: minutes,
        }).then(res => res.data.data);
    }

    // `inStock` narrows every tab to products that currently have stock -- a big
    // marketplace catalog is mostly dead listings, so this is how a merchant
    // finds the few hundred products worth tracking. It must be passed to
    // getProductCounts and trackAllMatching too, or "select all N" would promise
    // (and track) products the filtered list never showed.
    listProducts(campaignId, page = 0, query = '', status = '', inStock = false) {
        return AuthService.apiGet(this.url + campaignId + "/marketplace_products/", {
            params: {page: page, query: query, status: status, in_stock: inStock},
        }).then(res => res.data.data);
    }

    updateTracking(campaignId, activate, deactivate) {
        return AuthService.apiPost(this.url + campaignId + "/marketplace_tracking/", {
            activate: activate,
            deactivate: deactivate,
        }).then(res => res.data.data);
    }

    // "Select all N" across pages: track/untrack EVERY product matching the
    // picker's current view (search query + status tab), not just the selected
    // page. The backend walks the catalog / DB, so this is heavier than a keyed
    // updateTracking; the dialog shows a spinner while it runs.
    trackAllMatching(campaignId, query, status, inStock = false) {
        return AuthService.apiPost(this.url + campaignId + "/marketplace_track_all/", {
            query: query, status: status, in_stock: inStock,
        }).then(res => res.data.data);
    }

    // Picker-header counts: tracked (active), deactivated (pasif), and untracked
    // (in the marketplace catalog but never added). Untracked is exact -- the
    // backend walks the whole catalog -- so this is slower than listProducts; the
    // dialog loads it asynchronously and shows a spinner until it resolves.
    getProductCounts(campaignId, inStock = false) {
        return AuthService.apiGet(this.url + campaignId + "/marketplace_product_counts/", {
            params: {in_stock: inStock},
        }).then(res => res.data.data);
    }

    // Cheap, accurate tracked-product count for the onboarding summary panel
    // (Task 18). Reuses the SAME endpoint the campaign list cards use for
    // "Product Count" (see Campaign.js / CampaignService.countActiveAndInactiveProducts),
    // keyed by campaign id. This is accurate for marketplace campaigns too:
    // tracking a marketplace product writes a real Product document
    // (MarketplaceCampaignService.update_tracking / _upsert_owner_product on
    // the backend), so it is counted the same way as any other product --
    // no separate marketplace-specific counting endpoint is needed.
    getTrackedCount(campaignId) {
        return AuthService.apiGet(this.url + "count_active_and_inactive_products/")
            .then(res => (res.data.data && res.data.data[campaignId] && res.data.data[campaignId].product_count) || 0);
    }
}
